import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OrganizationDetail, OrganizationFeatureItem, OrganizationPipeline, OrganizationChannelRelation } from './organization.model';
import { QueryParams, QueryResult } from '../../../core/store/models';
import { HttpService } from '../../../core/services/http.service';
import { Organization } from 'src/app/modules/core/store/models/organization.model';
import { Language } from 'src/app/modules/core/languages/language.model';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';
import { PipelineDefinitionSystemSettings } from '../datafeeds/datafeed.model';

@Injectable({
  providedIn: 'root'
})
export class OrganizationApiService {
  constructor(private http: HttpService) {}

  public createOrganization(organization: OrganizationDetail): Observable<OrganizationDetail> {
    const url = `${apiBaseUrl}/v1/iam/organizations/`;

    return this.http.post(url, organization);
  }

  public getOrganizationSelfDetails(organizationUId: string): Observable<QueryResult<OrganizationDetail>> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/get-self-details`;

    return this.http.get<QueryResult<OrganizationDetail>>(url);
  }

  public getOrganizationForSelf(organizationUId: string): Observable<OrganizationDetail> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/get-self`;

    return this.http.get<OrganizationDetail>(url);
  }

  public getOrganizationsSeeAll(params: QueryParams): Observable<QueryResult<OrganizationDetail>> {
    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/iam/management/organizations?${queryString}`;

    return this.http.get<QueryResult<OrganizationDetail>>(url);
  }

  public getOrganization(organizationUId: string): Observable<Organization> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}`;

    return this.http.get(url);
  }

  public deleteOrganization(organization: OrganizationDetail): Observable<void> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organization.uId}`;

    return this.http.delete(url);
  }

  public updateOrganization(organization: OrganizationDetail): Observable<OrganizationDetail> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organization.uId}`;

    return this.http.put(url, organization);
  }

  public getOrganizationFeatureItems(organizationUId: string): Observable<OrganizationFeatureItem[]> {
    const url = `${apiBaseUrl}/v1/iam/management/organizations/${organizationUId}/features`;

    return this.http.get(url);
  }

  public updateOrganizationFeatureItems(organizationUId: string, organizationFeatureItems: OrganizationFeatureItem[]): Observable<void[]> {
    const url = `${apiBaseUrl}/v1/iam/management/organizations/${organizationUId}/features`;

    return this.http.put(url, organizationFeatureItems);
  }

  public getOrganizationLanguages(organizationUId: string): Observable<Language[]> {
    const url = `${apiBaseUrl}/v1/iam/languages/${organizationUId}`;

    return this.http.get(url);
  }

  public getAllOrganizationPipelines(organizationUId: string, params: QueryParams): Observable<QueryResult<OrganizationPipeline>> {
    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/pipelines/pipeline-definitions/organization/${organizationUId}?${queryString}`;

    return this.http.get<QueryResult<OrganizationPipeline>>(url);
  }

  public addPipelineDefinitionToOrganization(organizationUId: string, pipelineUId: string): Observable<void> {
    const url = `${apiBaseUrl}/v1/pipelines/pipeline-definitions/organization/${organizationUId}/${pipelineUId}`;

    return this.http.post(url, {});
  }

  public removePipelineDefinitionFromOrganization(organizationUId: string, pipelineUId: string): Observable<void> {
    const url = `${apiBaseUrl}/v1/pipelines/pipeline-definitions/organization/${organizationUId}/${pipelineUId}`;

    return this.http.delete(url, {});
  }

  public updatePipelineDefinitionSystemSettings(organizationUId: string, pipelineUId: string, settings: PipelineDefinitionSystemSettings): Observable<void> {
    const url = `${apiBaseUrl}/v1/pipelines/pipeline-definitions/organization/${organizationUId}/${pipelineUId}/system-settings`;

    return this.http.post(url, settings);
  }

  public getAllOrganizationChannels(organizationUId: string, params: QueryParams): Observable<QueryResult<OrganizationChannelRelation>> {
    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/notifications/OrganizationChannels/organization/${organizationUId}/all?${queryString}`;

    return this.http.get<QueryResult<OrganizationPipeline>>(url);
  }

  public addChannelToOrganization(organizationUId: string, channelUId: string): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/OrganizationChannels/organization/${organizationUId}/channels/${channelUId}`;

    return this.http.post(url, {});
  }

  public removeChannelFromOrganization(organizationUId: string, channelUId: string): Observable<void> {
    const url = `${apiBaseUrl}/v1/notifications/OrganizationChannels/organization/${organizationUId}/channels/${channelUId}`;

    return this.http.delete(url, {});
  }
}
